import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
  Avatar,
  Fade,
  Stack,
  alpha,
  useTheme,
  useMediaQuery,
  Card
  // CardContent,
  // Divider,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Refresh,
  ContentCopy,
  Security,
  Speed,
  Schedule,
  ExpandMore,
  CheckCircle,
  Cancel,
  VpnKey,
  Assessment,
  Info
  // Close,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

interface Permissions {
  'prompt.process': boolean;
  'prompt.check': boolean;
  'prompt.history': boolean;
  'dashboard.view': boolean;
  'rules.manage': boolean;
}

interface ApiKey {
  id: number;
  name: string;
  permissions: Permissions;
  rate_limit: number;
  rate_limit_window: number;
  is_active: boolean;
  last_used: string | null;
  usage_count: number;
  allowed_ips: string[];
  allowed_domains: string[];
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  is_expired: boolean;
  key_preview: string;
}

interface NewApiKey extends ApiKey {
  key: string;
}

const ApiKeys: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const [newKeyData, setNewKeyData] = useState<NewApiKey | null>(null);
  const [showNewKey, setShowNewKey] = useState(false);
  const { isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    rate_limit: 100,
    rate_limit_window: 3600,
    expires_in_days: '',
    allowed_ips: '',
    allowed_domains: '',
    permissions: {
      'prompt.process': true,
      'prompt.check': true,
      'prompt.history': true,
      'dashboard.view': false,
      'rules.manage': false
    }
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchApiKeys();
    }
  }, [isAuthenticated]);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/keys');
      setApiKeys(response.data.api_keys || []);
    } catch (error: any) {
      console.error('Failed to fetch API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      const payload = {
        ...formData,
        allowed_ips: formData.allowed_ips
          ? formData.allowed_ips.split(',').map((ip) => ip.trim())
          : [],
        allowed_domains: formData.allowed_domains
          ? formData.allowed_domains.split(',').map((domain) => domain.trim())
          : [],
        expires_in_days: formData.expires_in_days ? parseInt(formData.expires_in_days) : null
      };

      const response = await axios.post('/api/keys', payload);
      setNewKeyData(response.data.api_key);
      setShowNewKey(true);
      setCreateDialogOpen(false);
      fetchApiKeys();
      toast.success('API key created successfully!');
      resetForm();
    } catch (error: any) {
      console.error('Failed to create API key:', error);
      toast.error(error.response?.data?.error || 'Failed to create API key');
    }
  };

  const handleUpdateKey = async () => {
    if (!selectedKey) return;

    try {
      const payload = {
        name: formData.name,
        permissions: formData.permissions,
        rate_limit: formData.rate_limit,
        rate_limit_window: formData.rate_limit_window,
        allowed_ips: formData.allowed_ips
          ? formData.allowed_ips.split(',').map((ip) => ip.trim())
          : [],
        allowed_domains: formData.allowed_domains
          ? formData.allowed_domains.split(',').map((domain) => domain.trim())
          : []
      };

      await axios.put(`/api/keys/${selectedKey.id}`, payload);
      setEditDialogOpen(false);
      fetchApiKeys();
      toast.success('API key updated successfully!');
      resetForm();
    } catch (error: any) {
      console.error('Failed to update API key:', error);
      toast.error(error.response?.data?.error || 'Failed to update API key');
    }
  };

  const handleDeleteKey = async (keyId: number) => {
    if (
      !window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')
    ) {
      return;
    }

    try {
      await axios.delete(`/api/keys/${keyId}`);
      fetchApiKeys();
      toast.success('API key deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete API key:', error);
      toast.error(error.response?.data?.error || 'Failed to delete API key');
    }
  };

  const handleRegenerateKey = async (keyId: number) => {
    if (
      !window.confirm(
        'Are you sure you want to regenerate this API key? The old key will stop working immediately.'
      )
    ) {
      return;
    }

    try {
      const response = await axios.post(`/api/keys/${keyId}/regenerate`);
      setNewKeyData(response.data.api_key);
      setShowNewKey(true);
      fetchApiKeys();
      toast.success('API key regenerated successfully!');
    } catch (error: any) {
      console.error('Failed to regenerate API key:', error);
      toast.error(error.response?.data?.error || 'Failed to regenerate API key');
    }
  };

  const handleEditKey = (key: ApiKey) => {
    setSelectedKey(key);
    setFormData({
      name: key.name,
      rate_limit: key.rate_limit,
      rate_limit_window: key.rate_limit_window,
      expires_in_days: '',
      allowed_ips: key.allowed_ips.join(', '),
      allowed_domains: key.allowed_domains.join(', '),
      permissions: { ...key.permissions }
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      rate_limit: 100,
      rate_limit_window: 3600,
      expires_in_days: '',
      allowed_ips: '',
      allowed_domains: '',
      permissions: {
        'prompt.process': true,
        'prompt.check': true,
        'prompt.history': true,
        'dashboard.view': false,
        'rules.manage': false
      }
    });
    setSelectedKey(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusChip = (key: ApiKey) => {
    if (key.is_expired) {
      return <Chip label="Expired" color="error" size="small" icon={<Cancel />} />;
    }
    if (!key.is_active) {
      return <Chip label="Inactive" color="default" size="small" icon={<Cancel />} />;
    }
    return <Chip label="Active" color="success" size="small" icon={<CheckCircle />} />;
  };

  const permissionLabels: Record<keyof Permissions, string> = {
    'prompt.process': 'Process Prompts',
    'prompt.check': 'Check Prompts',
    'prompt.history': 'View History',
    'dashboard.view': 'View Dashboard',
    'rules.manage': 'Manage Rules'
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          gap={2}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="body1" color="text.secondary">
            Loading API keys...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Enhanced Header with Gradient Background */}
      <Fade in timeout={600}>
        <Box
          sx={{
            mb: 4,
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '300px',
              height: '300px',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              borderRadius: '50%',
              transform: 'translate(30%, -30%)'
            }
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={3}
          >
            <Box display="flex" alignItems="center" gap={2.5}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`
                }}
              >
                <VpnKey sx={{ fontSize: 32, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  fontWeight={700}
                  sx={{
                    mb: 0.5,
                    background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  API Key Management
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Security sx={{ fontSize: 16 }} />
                  Manage your API keys for programmatic access to GuardLLM
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
              size={isMobile ? 'medium' : 'large'}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                },
                transition: 'all 0.3s ease'
              }}
            >
              Create API Key
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* Info Alert */}
      <Fade in timeout={800}>
        <Alert
          severity="info"
          icon={<Info />}
          sx={{
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
          }}
        >
          API keys allow external applications to interact with your security services. Keep your
          keys secure and never share them publicly.
        </Alert>
      </Fade>

      {/* Enhanced API Keys Table */}
      <Fade in timeout={1000}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
              borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              API Keys
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {apiKeys.length} key{apiKeys.length !== 1 ? 's' : ''} configured
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Rate Limit</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Usage</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Last Used</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2
                        }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
                          }}
                        >
                          <VpnKey sx={{ fontSize: 40, color: 'primary.main' }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600} color="text.primary">
                          No API Keys Found
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ maxWidth: 400, textAlign: 'center' }}
                        >
                          Create your first API key to get started with programmatic access
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => setCreateDialogOpen(true)}
                          sx={{
                            mt: 1,
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                              transform: 'translateY(-2px)',
                              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Create API Key
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  apiKeys.map((key) => (
                    <TableRow
                      key={key.id}
                      hover
                      sx={{
                        transition: 'all 0.2s ease',
                        borderLeft: `3px solid transparent`,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          borderLeft: `3px solid ${theme.palette.primary.main}`
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {key.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.primary"
                            sx={{
                              fontFamily: 'monospace',
                              bgcolor: alpha(theme.palette.action.hover, 0.2),
                              px: 1,
                              py: 0.25,
                              borderRadius: 0.5,
                              display: 'inline-block',
                              mt: 0.5
                            }}
                          >
                            {key.key_preview}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusChip(key)}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette.info.main, 0.1)
                            }}
                          >
                            <Speed sx={{ fontSize: 18, color: 'info.main' }} />
                          </Box>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {key.rate_limit}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              per {Math.floor(key.rate_limit_window / 3600)}h
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.success.main, 0.1)
                          }}
                        >
                          <Assessment sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            {key.usage_count}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {key.last_used ? formatDate(key.last_used) : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {formatDate(key.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditKey(key)}
                              sx={{
                                bgcolor: alpha(theme.palette.warning.main, 0.08),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.warning.main, 0.16),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Regenerate">
                            <IconButton
                              size="small"
                              onClick={() => handleRegenerateKey(key.id)}
                              sx={{
                                bgcolor: alpha(theme.palette.info.main, 0.08),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.info.main, 0.16),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Refresh fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteKey(key.id)}
                              sx={{
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.16),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Fade>

      {/* Create API Key Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Production App, Development Testing"
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Rate Limit (requests)"
                type="number"
                value={formData.rate_limit}
                onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 10000 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Time Window (seconds)"
                type="number"
                value={formData.rate_limit_window}
                onChange={(e) =>
                  setFormData({ ...formData, rate_limit_window: parseInt(e.target.value) })
                }
                inputProps={{ min: 60, max: 86400 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Expires in Days (optional)"
                type="number"
                value={formData.expires_in_days}
                onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                placeholder="Leave empty for no expiration"
                inputProps={{ min: 1, max: 365 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Permissions
              </Typography>
              <FormGroup>
                {Object.entries(permissionLabels).map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={formData.permissions[key as keyof Permissions] || false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            permissions: {
                              ...formData.permissions,
                              [key as keyof Permissions]: e.target.checked
                            }
                          })
                        }
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Advanced Security Settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Allowed IP Addresses (optional)"
                        value={formData.allowed_ips}
                        onChange={(e) => setFormData({ ...formData, allowed_ips: e.target.value })}
                        placeholder="192.168.1.1, 10.0.0.1"
                        helperText="Comma-separated list of IP addresses. Leave empty to allow all IPs."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Allowed Domains (optional)"
                        value={formData.allowed_domains}
                        onChange={(e) =>
                          setFormData({ ...formData, allowed_domains: e.target.value })
                        }
                        placeholder="example.com, api.myapp.com"
                        helperText="Comma-separated list of domains. Leave empty to allow all domains."
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateKey} variant="contained" disabled={!formData.name.trim()}>
            Create API Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit API Key Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit API Key</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Rate Limit (requests)"
                type="number"
                value={formData.rate_limit}
                onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 10000 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Time Window (seconds)"
                type="number"
                value={formData.rate_limit_window}
                onChange={(e) =>
                  setFormData({ ...formData, rate_limit_window: parseInt(e.target.value) })
                }
                inputProps={{ min: 60, max: 86400 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Permissions
              </Typography>
              <FormGroup>
                {Object.entries(permissionLabels).map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={formData.permissions[key as keyof Permissions] || false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            permissions: {
                              ...formData.permissions,
                              [key as keyof Permissions]: e.target.checked
                            }
                          })
                        }
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Allowed IP Addresses"
                value={formData.allowed_ips}
                onChange={(e) => setFormData({ ...formData, allowed_ips: e.target.value })}
                placeholder="192.168.1.1, 10.0.0.1"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Allowed Domains"
                value={formData.allowed_domains}
                onChange={(e) => setFormData({ ...formData, allowed_domains: e.target.value })}
                placeholder="example.com, api.myapp.com"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateKey} variant="contained">
            Update API Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* New API Key Display Dialog */}
      <Dialog open={showNewKey} onClose={() => setShowNewKey(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Security color="success" />
            API Key Created Successfully
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Important:</strong> This is the only time you'll see this API key. Save it
            securely as it cannot be recovered.
          </Alert>

          {newKeyData && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Your New API Key:
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                  mb: 2
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                      color: theme.palette.mode === 'dark' ? 'grey.100' : 'text.primary'
                    }}
                  >
                    {newKeyData.key}
                  </Typography>
                  <Tooltip title="Copy to clipboard">
                    <IconButton onClick={() => copyToClipboard(newKeyData.key)}>
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>

              <Typography variant="h6" gutterBottom>
                Key Details:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Security />
                  </ListItemIcon>
                  <ListItemText primary="Name" secondary={newKeyData.name} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Speed />
                  </ListItemIcon>
                  <ListItemText
                    primary="Rate Limit"
                    secondary={`${newKeyData.rate_limit} requests per ${Math.floor(newKeyData.rate_limit_window / 3600)} hour(s)`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Schedule />
                  </ListItemIcon>
                  <ListItemText
                    primary="Expires"
                    secondary={newKeyData.expires_at ? formatDate(newKeyData.expires_at) : 'Never'}
                  />
                </ListItem>
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewKey(false)} variant="contained">
            I've Saved It Securely
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApiKeys;
